<template>
    <Modal size="xl" @close="closeModal">
        <template #header>
            <div class="text-gray-600">{{ title }}</div>
        </template>

        <template #body>
            <div class="w-full px-[26px] pb-6 pt-[26px]">
                <div class="text-sm text-gray-500">
                    <span class="font-medium">{{
                        $t("vue.two_factor_authentication_enabled")
                    }}</span>
                    Store these recovery codes in a secure password manager.
                    They cand be used to recover access to your account when you
                    are not able to authenticate using your device.
                </div>

                <div
                    ref="input"
                    class="relative mt-7 rounded-md border border-gray-300 bg-gray-50 px-6 pb-[18px] pt-3 text-sm leading-7 text-gray-800 shadow-sm"
                >
                    <p
                        v-for="recoveryCode in recoveryCodes"
                        :key="recoveryCode"
                    >
                        {{ recoveryCode }}
                    </p>

                    <transition
                        enter-active-class="transition duration-100 ease-out transform"
                        enter-from-class="scale-95 opacity-0"
                        enter-to-class="scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in transform"
                        leave-from-class="scale-100 opacity-100"
                        leave-to-class="scale-95 opacity-0"
                    >
                        <Tooltip
                            v-if="copiedSuccess"
                            class="absolute top-[-55px]"
                            style="left: 50%; transform: translate(-50%, 0)"
                        >
                            Copied to clipboard!
                        </Tooltip>
                    </transition>
                </div>

                <AtomicButton
                    class="mt-5"
                    color="white"
                    size="sm"
                    @click="copyToClipboard"
                >
                    <template #icon>
                        <DocumentDuplicateIcon
                            class="mr-2 h-5 w-5 text-gray-400 transition duration-200"
                        />
                    </template>
                    Copy to clipboard
                </AtomicButton>
                <AtomicButton
                    v-if="canRegenerateCodes"
                    class="ml-4 mt-5"
                    color="white"
                    size="sm"
                    @click="generateNewCodes"
                >
                    <template #icon>
                        <svg
                            class="mr-2"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4.5 5.101V6.32526L5.35705 5.45103C6.12967 4.6629 7.0902 4.08442 8.14815 3.77007C9.20611 3.45572 10.3267 3.41585 11.4043 3.65421C12.4819 3.89256 13.4811 4.40131 14.3078 5.13252C15.1345 5.86373 15.7615 6.79334 16.1296 7.83379L16.1295 7.83386L16.1345 7.84687C16.1585 7.90925 16.1698 7.97582 16.1677 8.04264C16.1656 8.10947 16.1501 8.17518 16.1221 8.2359C16.0941 8.29663 16.0543 8.35113 16.0049 8.39618C15.9555 8.44123 15.8976 8.47592 15.8345 8.49819C15.7715 8.52046 15.7046 8.52986 15.6379 8.52584C15.5712 8.52182 15.5059 8.50446 15.446 8.47478C15.3861 8.44511 15.3327 8.40372 15.2891 8.35306C15.2455 8.3024 15.2124 8.24351 15.192 8.17987L15.1898 8.17302L15.1874 8.16623C14.8553 7.22776 14.2749 6.39696 13.5079 5.76235C12.741 5.12774 11.8162 4.71311 10.8321 4.56263C9.84811 4.41215 8.84168 4.53146 7.92011 4.90786C6.99853 5.28426 6.19634 5.90363 5.59902 6.69998L4.99894 7.5H5.999H9C9.13261 7.5 9.25978 7.55268 9.35355 7.64645C9.44732 7.74022 9.5 7.86739 9.5 8C9.5 8.13261 9.44732 8.25978 9.35355 8.35355C9.25978 8.44732 9.13261 8.5 9 8.5H4C3.86739 8.5 3.74021 8.44732 3.64645 8.35355C3.55268 8.25979 3.5 8.13261 3.5 8V3C3.5 2.86739 3.55268 2.74021 3.64645 2.64645C3.74021 2.55268 3.86739 2.5 4 2.5C4.13261 2.5 4.25978 2.55268 4.35355 2.64645C4.44732 2.74021 4.5 2.86739 4.5 3V5.101ZM4.1754 11.5281C4.23706 11.5065 4.30237 11.4971 4.36763 11.5006C4.43321 11.5041 4.49745 11.5205 4.55669 11.5488C4.61594 11.5771 4.66903 11.6168 4.71292 11.6657C4.75681 11.7145 4.79066 11.7715 4.81252 11.8335L4.81263 11.8338C5.14467 12.7722 5.7251 13.603 6.49207 14.2377C7.25904 14.8723 8.18382 15.2869 9.16786 15.4374C10.1519 15.5879 11.1583 15.4685 12.0799 15.0921C13.0015 14.7157 13.8037 14.0964 14.401 13.3L15.0011 12.5H14.001H11C10.8674 12.5 10.7402 12.4473 10.6464 12.3536C10.5527 12.2598 10.5 12.1326 10.5 12C10.5 11.8674 10.5527 11.7402 10.6464 11.6464C10.7402 11.5527 10.8674 11.5 11 11.5H16C16.1326 11.5 16.2598 11.5527 16.3536 11.6464C16.4473 11.7402 16.5 11.8674 16.5 12V17C16.5 17.1326 16.4473 17.2598 16.3536 17.3536C16.2598 17.4473 16.1326 17.5 16 17.5C15.8674 17.5 15.7402 17.4473 15.6464 17.3536C15.5527 17.2598 15.5 17.1326 15.5 17V14.899V13.6747L14.643 14.549C13.8703 15.3371 12.9098 15.9156 11.8518 16.2299C10.7939 16.5443 9.67333 16.5842 8.5957 16.3458C7.51808 16.1074 6.51886 15.5987 5.69217 14.8675C4.86548 14.1363 4.23853 13.2067 3.87036 12.1662C3.84856 12.1044 3.83913 12.0388 3.84262 11.9734C3.84611 11.9078 3.86249 11.8435 3.89081 11.7843C3.91913 11.7251 3.95885 11.672 4.00769 11.6281C4.05653 11.5842 4.11354 11.5503 4.17546 11.5285L4.1754 11.5281Z"
                                fill="#9FA6B2"
                                stroke="#9FA6B2"
                            />
                        </svg>
                    </template>

                    Generate new codes
                </AtomicButton>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end border-t border-gray-200 p-6">
                <AtomicButton color="yellow" size="sm" @click="closeModal">
                    Finish
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import axios from "axios";
import { useClipboard } from "@vueuse/core";
import { DocumentDuplicateIcon } from "@heroicons/vue/24/solid";

defineProps({
    title: {
        type: String,
        default: "Recovery codes",
    },
    canRegenerateCodes: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["close"]);

const recoveryCodes = ref([]);
const copiedSuccess = ref(false);

onMounted(() => {
    getRecoveryCodes();
});

const getRecoveryCodes = () => {
    axios.get("/user/two-factor-recovery-codes").then((response) => {
        recoveryCodes.value = response.data;
    });
};

const generateNewCodes = () => {
    axios.post("/user/two-factor-recovery-codes").then(() => {
        getRecoveryCodes();
    });
};

const copyToClipboard = () => {
    const clipboard = useClipboard();
    clipboard.copy(recoveryCodes.value.join("\r\n"));
    copiedSuccess.value = true;
    setTimeout(() => {
        copiedSuccess.value = false;
    }, 1000);
};

const closeModal = () => {
    recoveryCodes.value = [];
    emit("close");
};
</script>
